// store.ts — typed front-end wrappers over the on-device Rust SQLite commands.
// Every call hits LOCAL SQLite via Tauri IPC (durable, offline, on-disk). Nothing
// here leaves the device — only the derived-signals slice goes to the brain (brain.ts).
//
// HOUSEHOLD-CHORE domain — OBSERVATIONAL fence: this app holds the chores whoever runs the place set up
// and the completions they logged ONLY. No behaviour score, no "good/bad member" field,
// no reward/punishment field exists in any type below. Points are a plain tally.

import { invoke } from "./tauri-web";

// ── Member ──────────────────────────────────────────────────────────────────────
export interface Member {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}
export interface NewMember {
  name: string;
}
export type MemberPatch = Partial<{ name: string }>;

// ── Chore ────────────────────────────────────────────────────────────────────
export interface Chore {
  id: string;
  member_id: string;
  name: string;
  /** Plain points tally the one set for this chore (≥0). */
  points: number;
  /** "once" | "daily" | "weekly" (free text — anyone can type their own). */
  cadence: string;
  /** 1 = active (shown + loggable), 0 = retired (kept for history, hidden). */
  active: number;
  /** Comma-separated member_ids this chore rotates between ("" = a normal fixed chore).
   *  `member_id` above is whose turn it is NOW; logging a completion advances it. */
  rotation: string;
  /** Explicit calendar deadline "YYYY-MM-DD" ("" = none). Coexists with cadence. */
  due_on: string;
  created_at: string;
  updated_at: string;
}
export interface NewChore {
  member_id: string;
  name: string;
  points?: number;
  cadence?: string;
  rotation?: string;
  due_on?: string;
}
export type ChorePatch = Partial<{ name: string; points: number; cadence: string; active: number; rotation: string; member_id: string; due_on: string }>;

// ── Fair-share distribution goal ───────────────────────────────────────────────
// The explicit target the paying chore apps (Tody's FairShare, Flatastic) charge for: the
// household says "no one should be carrying more than N% of the week", and the board flags
// anyone over it. Stored as a percent string in settings; 60 is the FairShare-style default.
export const FAIR_SHARE_GOAL_KEY = "fair_share_goal";
export const FAIR_SHARE_GOAL_DEFAULT = 60;
/** Clamp to a sane band: below 34% no one could ever meet it with 3+ people; 100 disables flagging. */
export function clampGoal(pct: number): number {
  if (!Number.isFinite(pct)) return FAIR_SHARE_GOAL_DEFAULT;
  return Math.min(100, Math.max(34, Math.round(pct)));
}
/** Who is over the goal, given the balance rows. Empty when the split is fine or there's no data. */
export function overShare<T extends { member: { id: string; name: string }; share: number }>(
  balance: T[],
  goalPct: number,
): T[] {
  return balance.filter((b) => b.share * 100 > goalPct + 0.5);
}

/** Order of members in a rotating chore's pool. */
export const rotationOrder = (chore: { rotation: string }): string[] =>
  chore.rotation ? chore.rotation.split(",").map((s) => s.trim()).filter(Boolean) : [];

/** The member_id whose turn is AFTER the current one, wrapping around. Null if not rotating. */
export function nextInRotation(chore: { rotation: string; member_id: string }): string | null {
  const order = rotationOrder(chore);
  if (order.length < 2) return null;
  const i = order.indexOf(chore.member_id);
  return order[(i + 1) % order.length] ?? order[0];
}

// ── Completion (a logged "chore done") ────────────────────────────────────────
export interface Completion {
  id: string;
  member_id: string;
  chore_id: string;
  done_on: string;
  points: number;
  note: string;
  /** On-device photo proof (compressed-JPEG data-URL), or "". Never sent to AI. */
  photo: string;
  created_at: string;
}
/** A completion joined to its chore's name/cadence — what the list + signals read. */
export interface CompletionView extends Completion {
  chore_name: string;
  cadence: string;
}
export interface NewCompletion {
  member_id: string;
  chore_id: string;
  /** Defaults to today (local) when omitted. */
  done_on?: string;
  /** Defaults to the chore's current points when omitted. */
  points?: number;
  note?: string;
  /** Optional on-device photo proof (data-URL). */
  photo?: string;
}
export type CompletionPatch = Partial<{ done_on: string; points: number; note: string }>;

// ── Derived signals (computed on-device in Rust; only aggregates go to the brain) ──
// Mirrors src-tauri/src/signals.rs::MemberSignals. Every field is a computed count, points
// sum, date, or name — never a raw note. This is the payload the Weekly Recap reasons
// over (see brain.ts) so it can name SPECIFIC numbers, never re-read the log.
export interface Overall {
  total_completions: number;
  total_points: number;
  active_chores: number;
  first_logged: string | null;
  last_logged: string | null;
  days_span: number;
  unit_note: string;
}
export interface Period {
  from: string;
  to: string;
  completions: number;
  points: number;
  active_days: number;
  distinct_chores: number;
}
export interface ChoreTally {
  chore_id: string;
  name: string;
  cadence: string;
  points_each: number;
  active: boolean;
  total_count: number;
  total_points: number;
  period_count: number;
  last_done_on: string | null;
}
export interface Streak {
  current_days: number;
  anchor_day: string | null;
  counts_from_today: boolean;
  longest_days: number;
}
export interface HeadlineFact {
  kind: "period" | "top" | "least" | "streak" | "span";
  text: string;
  tone: "neutral" | "active" | "quiet";
}
export interface MemberSignals {
  schema: string;
  generated_for: string;
  member_name: string;
  period_days: number;
  overall: Overall;
  period: Period;
  per_chore: ChoreTally[];
  streak: Streak;
  headline_facts: HeadlineFact[];
}

export const store = {
  // Members
  listMembers: () => invoke<Member[]>("list_members"),
  getMember: (id: string) => invoke<Member | null>("get_member", { id }),
  createMember: (input: NewMember) => invoke<Member>("create_member", { input }),
  updateMember: (id: string, patch: MemberPatch) => invoke<void>("update_member", { id, patch }),
  deleteMember: (id: string) => invoke<void>("delete_member", { id }),
  // Chores
  listChores: (memberId?: string, activeOnly = false) =>
    invoke<Chore[]>("list_chores", { memberId: memberId ?? null, activeOnly }),
  getChore: (id: string) => invoke<Chore | null>("get_chore", { id }),
  createChore: (input: NewChore) => invoke<Chore>("create_chore", { input }),
  updateChore: (id: string, patch: ChorePatch) => invoke<void>("update_chore", { id, patch }),
  deleteChore: (id: string) => invoke<void>("delete_chore", { id }),
  // Completions
  listCompletions: (memberId?: string) => invoke<CompletionView[]>("list_completions", { memberId: memberId ?? null }),
  getCompletion: (id: string) => invoke<Completion | null>("get_completion", { id }),
  createCompletion: (input: NewCompletion) => invoke<Completion>("create_completion", { input }),
  updateCompletion: (id: string, patch: CompletionPatch) => invoke<void>("update_completion", { id, patch }),
  deleteCompletion: (id: string) => invoke<void>("delete_completion", { id }),
  // Household preferences. `getSetting` returns null when unset — callers apply their own default.
  getSetting: (key: string) => invoke<string | null>("get_setting", { key }),
  setSetting: (key: string, value: string) => invoke<void>("set_setting", { key, value }),
  /** Compute the derived-signals object on-device for ONE member. `today` = client local YYYY-MM-DD. */
  computeSignals: (memberId: string, today: string, periodDays = 7) =>
    invoke<MemberSignals>("compute_signals", { memberId, today, periodDays }),
};

// ── Pure helpers (no I/O) ─────────────────────────────────────────────────────

/** Curated cadence suggestions; anyone can also type their own. */
export const CADENCES = ["once", "daily", "weekly"] as const;

/** Common chore points presets shown as quick-pick chips. */
export const POINT_PRESETS = [1, 2, 3, 5, 10] as const;

/** A small emoji for a chore, matched on a keyword in its name (purely decorative). */
export function emojiForChore(name: string): string {
  const n = name.trim().toLowerCase();
  const map: [RegExp, string][] = [
    [/dish|plate|wash up|kitchen/, "🍽️"],
    [/trash|bin|rubbish|garbage/, "🗑️"],
    [/bed|tidy room|room|clean.*room/, "🛏️"],
    [/laundry|wash|clothes|fold/, "🧺"],
    [/vacuum|hoover|sweep|mop|floor/, "🧹"],
    [/dog|cat|pet|feed|walk/, "🐾"],
    [/plant|water|garden|yard|lawn/, "🌱"],
    [/homework|read|study|practice/, "📚"],
    [/teeth|brush|shower|bath/, "🪥"],
    [/table|set the table|dinner/, "🍴"],
    [/car|wash the car/, "🚗"],
  ];
  for (const [re, emoji] of map) if (re.test(n)) return emoji;
  return "⭐";
}

/** Human label for a cadence (capitalised), with a sensible fallback. */
export function cadenceLabel(cadence: string): string {
  const c = cadence.trim().toLowerCase();
  if (c === "once") return "One-off";
  if (c === "daily") return "Daily";
  if (c === "weekly") return "Weekly";
  return cadence.trim() ? cadence.trim().charAt(0).toUpperCase() + cadence.trim().slice(1) : "—";
}

const toLocalDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

/**
 * 🔴 THE ONE cadence vocabulary. Days a cadence repeats on; null = not recurring (a one-off, or
 * a word we don't schedule on).
 *
 * There used to be TWO of these — this one, and a private `cadenceDays()` inside App.tsx that
 * drives the board's ranking. They disagreed about "monthly": this one knew it (30), the board's
 * did not, so the board treated a monthly chore as a ONE-OFF — settled forever the moment it was
 * logged once, gone from the board, while the chore's own tile 200px away still said
 * "overdue 15d" (reproduced 2026-08-28, `probe-leads.mjs` LEAD 2). "monthly" is a first-class
 * cadence everywhere else in the app: it is stored as free text, `chat/brain.ts` advertises it
 * to the assistant as one of four (`"daily, weekly, monthly, or once"`), and the chat's own
 * chore search filters on this function. One vocabulary, used by both surfaces.
 */
export function cadencePeriodDays(cadence: string): number | null {
  const c = cadence.trim().toLowerCase();
  if (c === "daily") return 1;
  if (c === "weekly") return 7;
  if (c === "monthly") return 30;
  return null; // "once", or the user's own word
}

/** 🔴 THE ONE deadline rule — both the board and `choreDueStatus` call this, so "by when" is
 *  never implemented twice. An explicit calendar deadline WINS over cadence while it is still
 *  outstanding: cadence says HOW OFTEN, the deadline says BY WHEN. Once the chore is done on or
 *  after the deadline the deadline is MET and this returns null, so the cadence schedule resumes.
 *  Returns null when there is no deadline, it is met, or the date is unparseable. */
export function deadlineStatus(
  dueOn: string,
  lastDoneOn: string | null,
  today: string = todayLocal(),
): { status: "ok" | "due" | "overdue"; days: number } | null {
  const deadline = (dueOn ?? "").trim();
  if (!deadline) return null;
  if (lastDoneOn !== null && lastDoneOn >= deadline) return null; // met — cadence takes over again
  const from = toLocalDate(deadline), to = toLocalDate(today);
  if (!from || !to) return null;
  const diff = Math.round((to.getTime() - from.getTime()) / 86_400_000);
  if (diff < 0) return { status: "ok", days: -diff };
  if (diff === 0) return { status: "due", days: 0 };
  return { status: "overdue", days: diff };
}

/** Factual schedule status of a chore vs the cadence that was set — "due" / "overdue Nd" / "ok".
 * Observational only: states where it stands against its own schedule; gives no advice. Returns
 * null for one-off / free-text cadences (no recurring due). */
export function choreDueStatus(
  cadence: string,
  lastDoneOn: string | null,
  today: string = todayLocal(),
  dueOn: string = "",
): { status: "ok" | "due" | "overdue"; days: number } | null {
  const dl = deadlineStatus(dueOn, lastDoneOn, today);
  if (dl) return dl;
  const period = cadencePeriodDays(cadence);
  if (period === null) return null;
  if (!lastDoneOn) return { status: "due", days: 0 };
  const from = toLocalDate(lastDoneOn), to = toLocalDate(today);
  if (!from || !to) return null;
  const since = Math.round((to.getTime() - from.getTime()) / 86_400_000);
  if (since < period) return { status: "ok", days: period - since };
  if (since === period) return { status: "due", days: 0 };
  return { status: "overdue", days: since - period };
}

/** Friendly "Jun 14, 2026" from a YYYY-MM-DD string (UTC-safe display). */
export function prettyDate(iso: string): string {
  if (!iso) return "";
  const d = toLocalDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** "today" / "yesterday" / "3 days ago" / a date — relative day label for a done-on date. */
export function relativeDay(iso: string, today = new Date()): string {
  const d = toLocalDate(iso);
  if (!d) return iso;
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((t0.getTime() - d.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return prettyDate(iso);
}

/**
 * Client-side tally for ONE member over a window of `periodDays` (default 7), inclusive of
 * today. Mirrors signals.rs's Period so the home stat cards render instantly from the
 * already-loaded completions WITHOUT a round-trip. Pure logistics: counts + points only.
 */
export function periodTally(completions: CompletionView[], periodDays = 7, today = new Date()): {
  completions: number; points: number; activeDays: number;
} {
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const from = new Date(t0);
  from.setDate(from.getDate() - (Math.max(1, periodDays) - 1));
  let count = 0;
  let points = 0;
  const days = new Set<string>();
  for (const c of completions) {
    const d = toLocalDate(c.done_on);
    if (!d) continue;
    if (d >= from && d <= t0) {
      count += 1;
      points += c.points;
      days.add(c.done_on.slice(0, 10));
    }
  }
  return { completions: count, points, activeDays: days.size };
}

/** All-time points earned by a member from their completions. Plain sum. */
export function totalPoints(completions: CompletionView[]): number {
  return completions.reduce((sum, c) => sum + c.points, 0);
}

/**
 * Current streak of consecutive logged days, ending today (if logged today) or at the
 * latest logged day. Mirrors signals.rs::compute_streak so the home shows the same number
 * the recap narrates. A count of consecutive logged days — never a measure of the member.
 */
export function currentStreak(completions: CompletionView[], today = new Date()): {
  days: number; live: boolean;
} {
  const set = new Set<string>();
  for (const c of completions) set.add(c.done_on.slice(0, 10));
  if (set.size === 0) return { days: 0, live: false };
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const todayKey = formatLocalDate(t0);
  const live = set.has(todayKey);
  // Anchor at today if logged today, else at the latest logged day.
  let cursor = t0;
  if (!live) {
    const latest = [...set].sort().pop()!;
    const d = toLocalDate(latest);
    if (!d) return { days: 0, live: false };
    cursor = d;
  }
  let days = 0;
  while (set.has(formatLocalDate(cursor))) {
    days += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return { days, live };
}
